import type { CalculatorInputValues } from '../calculators/types';
import {
  buildMarginScenarios,
  calculateMarginDecision,
  calculateTargetMarginPrice,
  describeMarginDecision,
  type MarginDecisionInput,
} from '../calculators/marginDecision';
import {
  DecisionCardGrid,
  DecisionComparisonTable,
  DecisionInsight,
} from './DecisionSupport';

const currencyFormatter = new Intl.NumberFormat('ko-KR', {
  maximumFractionDigits: 0,
});

const percentageFormatter = new Intl.NumberFormat('ko-KR', {
  maximumFractionDigits: 1,
});

function numberValue(values: CalculatorInputValues, key: string): number {
  const value = values[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

function toMarginInput(values: CalculatorInputValues): MarginDecisionInput {
  return {
    salesPrice: numberValue(values, 'salesPrice'),
    cost: numberValue(values, 'cost'),
    platformFeeRate: numberValue(values, 'platformFeeRate'),
    paymentFeeRate: numberValue(values, 'paymentFeeRate'),
    shippingCost: numberValue(values, 'shippingCost'),
    packagingCost: numberValue(values, 'packagingCost'),
    adCost: numberValue(values, 'adCost'),
    otherCost: numberValue(values, 'otherCost'),
  };
}

function won(value: number): string {
  return `${currencyFormatter.format(Math.round(value))}원`;
}

function percent(value: number): string {
  return `${percentageFormatter.format(value)}%`;
}

export function MarginDecisionPanel({ values }: { values: CalculatorInputValues }) {
  const input = toMarginInput(values);
  const result = calculateMarginDecision(input);
  const targetInput = {
    cost: input.cost,
    platformFeeRate: input.platformFeeRate,
    paymentFeeRate: input.paymentFeeRate,
    shippingCost: input.shippingCost,
    packagingCost: input.packagingCost,
    adCost: input.adCost,
    otherCost: input.otherCost,
  };
  const targets = [30, 40, 50].map((targetMarginRate) => {
    const targetPrice = calculateTargetMarginPrice(targetInput, targetMarginRate);
    return {
      label: `목표 마진 ${targetMarginRate}%`,
      value: targetPrice === null ? '계산 불가' : won(targetPrice),
      detail: targetPrice === null
        ? '수수료율과 목표 마진의 합이 100% 이상입니다.'
        : result.marginRate >= targetMarginRate
          ? '현재 판매가가 이 목표를 이미 충족합니다.'
          : `현재 판매가보다 ${won(Math.max(0, targetPrice - result.salesPrice))} 높아야 합니다.`,
    };
  });
  const scenarios = buildMarginScenarios(input).map((scenario) => ({
    id: scenario.id,
    label: scenario.label,
    description: scenario.description,
    values: [
      won(scenario.result.salesPrice),
      won(scenario.result.totalCost),
      won(scenario.result.profit),
      percent(scenario.result.marginRate),
    ],
  }));

  return (
    <section className="decision-support" aria-labelledby="margin-decision-title">
      <div className="decision-support-heading">
        <p className="eyebrow">DECISION SUPPORT</p>
        <h2 id="margin-decision-title">가격·마진 의사결정</h2>
        <p>단순 마진율을 넘어 수수료와 주문당 비용을 반영해 가격 변화에 따른 수익성을 비교합니다.</p>
      </div>

      <DecisionInsight title="현재 조건 해석">
        <p>{describeMarginDecision(result)}</p>
        <p className="decision-caveat">
          이 결과는 입력한 주문당 비용만 반영한 추정치입니다. 인건비·임대료 같은 고정비, 세금, 환불·반품, 재고 손실 등 미입력 비용은 포함하지 않습니다.
        </p>
      </DecisionInsight>

      <DecisionCardGrid
        title="목표 마진별 최소 판매가"
        description="현재 수수료율과 주문당 고정비용을 유지한다고 가정한 최소 판매가입니다."
        items={targets}
      />

      <DecisionComparisonTable
        title="조건 변화 비교"
        description="현재 조건과 대표적인 가격·원가 변화를 같은 기준으로 비교합니다."
        columns={['판매가', '총 비용', '예상 이익', '마진율']}
        rows={scenarios}
      />
    </section>
  );
}
